import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import QRCode from "qrcode";

import { me, type CurrentUser } from "../api/auth.ts";
import { apiFetch } from "../api/client.ts";

interface Session {
  id: string;
  device_label: string | null;
  user_agent: string | null;
  ip_address: string | null;
  last_seen: string;
  created_at: string;
  revoked_at: string | null;
}

interface Passkey {
  id: string;
  nickname: string | null;
  created_at: string;
  last_used_at: string | null;
}

export default function SettingsPage() {
  const { i18n } = useTranslation();
  const { data: user } = useQuery({ queryKey: ["auth", "me"], queryFn: me });
  const qc = useQueryClient();

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">Settings</h1>

      {user && <ProfileForm user={user} onSaved={() => qc.invalidateQueries({ queryKey: ["auth", "me"] })} />}

      <section className="card">
        <h2 className="mb-3 text-lg font-semibold">Language</h2>
        <div className="flex gap-2">
          {["en", "he"].map((lng) => (
            <button key={lng} className={i18n.language === lng ? "btn" : "btn-secondary"} onClick={() => i18n.changeLanguage(lng)}>
              {lng.toUpperCase()}
            </button>
          ))}
        </div>
      </section>

      <section className="card">
        <h2 className="mb-3 text-lg font-semibold">Two-factor authentication</h2>
        {user?.totp_enabled ? (
          <DisableTotp onChanged={() => qc.invalidateQueries({ queryKey: ["auth", "me"] })} />
        ) : (
          <EnrollTotp onChanged={() => qc.invalidateQueries({ queryKey: ["auth", "me"] })} />
        )}
      </section>

      <section className="card">
        <h2 className="mb-3 text-lg font-semibold">Passkeys</h2>
        <Passkeys />
      </section>

      <section className="card">
        <h2 className="mb-3 text-lg font-semibold">Active sessions</h2>
        <Sessions />
      </section>
    </div>
  );
}

function EnrollTotp({ onChanged }: { onChanged: () => void }) {
  const [uri, setUri] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [qrUrl, setQrUrl] = useState<string | null>(null);
  const [recovery, setRecovery] = useState<string[]>([]);
  const [code, setCode] = useState("");
  const [msg, setMsg] = useState<string | null>(null);

  const start = async () => {
    const res = await apiFetch<{ provisioning_uri: string; secret: string; recovery_codes: string[] }>(
      "/auth/2fa/enroll",
      { method: "POST" },
    );
    setUri(res.provisioning_uri);
    setSecret(res.secret);
    setRecovery(res.recovery_codes);
    setQrUrl(await QRCode.toDataURL(res.provisioning_uri));
  };

  const verify = async () => {
    try {
      await apiFetch("/auth/2fa/verify", { method: "POST", body: JSON.stringify({ totp_code: code }) });
      setMsg("2FA enabled.");
      onChanged();
    } catch (e) {
      setMsg(String((e as Error).message));
    }
  };

  if (!uri) {
    return (
      <button type="button" className="btn" onClick={start}>
        Enroll 2FA
      </button>
    );
  }
  return (
    <div className="space-y-3">
      {qrUrl && <img src={qrUrl} alt="TOTP QR" className="mx-auto block h-40 w-40 max-w-[80vw] sm:h-44 sm:w-44" />}
      <p className="break-all text-sm text-slate-600">
        Manual secret: <code className="rounded bg-slate-100 px-2 py-1">{secret}</code>
      </p>
      <div>
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-semibold">Recovery codes (save now):</span>
          <button
            type="button"
            className="btn-secondary text-xs"
            onClick={() => navigator.clipboard?.writeText(recovery.join("\n"))}
          >
            Copy all
          </button>
        </div>
        <ul className="mt-1 grid grid-cols-2 gap-1 font-mono text-xs sm:grid-cols-3">
          {recovery.map((c) => (
            <li key={c} className="break-all">{c}</li>
          ))}
        </ul>
      </div>
      <label className="block text-sm">
        <span className="mb-1 block font-medium">Enter a 6-digit code to confirm</span>
        <input className="input max-w-xs" value={code} onChange={(e) => setCode(e.target.value)} />
      </label>
      <button type="button" className="btn" onClick={verify}>Verify + enable</button>
      {msg && <p className="text-sm text-slate-600">{msg}</p>}
    </div>
  );
}

function DisableTotp({ onChanged }: { onChanged: () => void }) {
  const [code, setCode] = useState("");
  const disable = async () => {
    await apiFetch("/auth/2fa/disable", { method: "POST", body: JSON.stringify({ totp_code: code }) });
    onChanged();
  };
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
      <label className="text-sm sm:flex-1">
        <span className="mb-1 block font-medium">Enter 6-digit code to disable</span>
        <input className="input" value={code} onChange={(e) => setCode(e.target.value)} />
      </label>
      <button type="button" className="btn-danger" onClick={disable}>Disable 2FA</button>
    </div>
  );
}

function Passkeys() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["passkeys"], queryFn: () => apiFetch<Passkey[]>("/auth/passkeys") });
  const del = useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/auth/passkeys/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["passkeys"] }),
  });
  return (
    <div>
      {data?.length ? (
        <ul className="divide-y divide-slate-100 text-sm">
          {data.map((p) => (
            <li key={p.id} className="flex items-center justify-between gap-3 py-2">
              <div className="min-w-0 flex-1">
                <div className="truncate">{p.nickname ?? "Unnamed passkey"}</div>
                <div className="text-xs text-slate-500">added {new Date(p.created_at).toLocaleDateString()}</div>
              </div>
              <button className="shrink-0 text-red-600 hover:underline" onClick={() => del.mutate(p.id)}>
                Remove
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-slate-500">No passkeys yet. Add one from a device that supports platform authenticators.</p>
      )}
    </div>
  );
}

function Sessions() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["sessions"], queryFn: () => apiFetch<Session[]>("/auth/sessions") });
  const revoke = useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/auth/sessions/${id}/revoke`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sessions"] }),
  });
  const revokeAll = useMutation({
    mutationFn: () => apiFetch<void>("/auth/sessions/revoke_all", { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sessions"] }),
  });
  return (
    <div className="space-y-3">
      <button type="button" className="btn-secondary" onClick={() => revokeAll.mutate()}>
        Revoke all other sessions
      </button>
      <ul className="divide-y divide-slate-100 text-sm">
        {data?.map((s) => (
          <li key={s.id} className="flex items-center justify-between gap-3 py-2">
            <div className="min-w-0 flex-1">
              <div className="truncate">{s.device_label ?? s.user_agent ?? "unknown device"}</div>
              <div className="truncate text-xs text-slate-500">
                {s.ip_address ?? ""} · last seen {new Date(s.last_seen).toLocaleString()}
                {s.revoked_at && <span className="ms-2 text-red-600">revoked</span>}
              </div>
            </div>
            {!s.revoked_at && (
              <button className="shrink-0 text-red-600 hover:underline" onClick={() => revoke.mutate(s.id)}>
                Revoke
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ProfileForm({ user, onSaved }: { user: CurrentUser; onSaved: () => void }) {
  const [fullName, setFullName] = useState(user.full_name);
  const [phone, setPhone] = useState(user.phone_e164 ?? "");
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      apiFetch("/auth/me", {
        method: "PATCH",
        body: JSON.stringify({ full_name: fullName, phone_e164: phone || null }),
      }),
    onSuccess: () => {
      setSaved(true);
      setError(null);
      onSaved();
      setTimeout(() => setSaved(false), 3000);
    },
    onError: (e) => setError((e as Error).message),
  });

  return (
    <section className="card">
      <h2 className="mb-3 text-lg font-semibold">Profile</h2>
      <form
        className="grid gap-3 sm:grid-cols-2"
        onSubmit={(e) => { e.preventDefault(); save.mutate(); }}
      >
        <label className="block text-sm">
          <span className="mb-1 block font-medium">Full name</span>
          <input className="input" value={fullName} onChange={(e) => setFullName(e.target.value)} required />
        </label>
        <label className="block text-sm">
          <span className="mb-1 block font-medium">Phone (E.164, for click-to-call)</span>
          <input className="input" placeholder="+17135551234" value={phone} onChange={(e) => setPhone(e.target.value)} />
        </label>
        <div className="flex items-center gap-3 sm:col-span-2">
          <button className="btn" type="submit">Save profile</button>
          {saved && <span className="text-sm text-emerald-700">Saved.</span>}
          {error && <span className="text-sm text-red-700">{error}</span>}
        </div>
      </form>
    </section>
  );
}
